import React, { useCallback, useMemo, useRef } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import { debounce } from "lodash";

import { searchBatchNumbers } from "data-handler/ducks/batchNumbers";
import { resolveBatchNumber } from "helpers/batchNumber";

export interface BatchOption {
  label: string;
  value: string;
}

interface Props {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  commodityId?: string;
  iso2?: string;
  /** YYYY-MM. The batch list is filtered by dispatch month. */
  dispatchMonth?: string;
  isOffline: boolean;
  /** Persisted batch numbers for this commodity, the offline source. */
  cachedBatchNumbers?: string[];
  placeholderOption: BatchOption;
  styles?: object;
  debounceMs?: number;
}

const toOptions = (batchNumbers: string[]): BatchOption[] =>
  batchNumbers.map((batchNumber) => ({
    label: batchNumber,
    value: batchNumber,
  }));

/**
 * Batch number picker for one delivery line.
 *
 * Lives on its own because its behaviour is all in the interplay between the
 * query it runs and react-select's lifecycle: it has to prefetch on open,
 * refetch when the commodity or the dispatch month moves, and never rewrite a
 * value it cannot see in the results.
 */
const BatchNumberSelect = ({
  value,
  onChange,
  onBlur,
  commodityId,
  iso2,
  dispatchMonth,
  isOffline,
  cachedBatchNumbers = [],
  placeholderOption,
  styles,
  debounceMs = 300,
}: Props) => {
  const loadedOptions = useRef<Set<string>>(new Set());

  const searchOffline = useCallback(
    (search: string) => {
      const needle = search.trim().toLowerCase();
      return toOptions(
        cachedBatchNumbers.filter((batchNumber) =>
          batchNumber.toLowerCase().includes(needle)
        )
      );
    },
    [cachedBatchNumbers]
  );

  // Rebuilt whenever the query behind it changes. Cancelling on a dependency
  // change instead would kill the prefetch the remount had just scheduled.
  const loadOptions = useMemo(
    () =>
      debounce((search: string, callback: (options: BatchOption[]) => void) => {
        const done = (options: BatchOption[]) => {
          loadedOptions.current = new Set(
            options.map((option) => option.value)
          );
          callback([placeholderOption, ...options]);
        };

        if (!commodityId) {
          done([]);
          return;
        }
        if (isOffline) {
          done(searchOffline(search));
          return;
        }
        searchBatchNumbers({
          commodityIds: [commodityId],
          iso2,
          date: dispatchMonth,
          search,
        })
          .then((results: { batch_number: string }[]) =>
            done(toOptions(results.map((result) => result.batch_number)))
          )
          .catch(() => done(searchOffline(search)));
      }, debounceMs),
    [
      commodityId,
      iso2,
      dispatchMonth,
      isOffline,
      searchOffline,
      placeholderOption,
      debounceMs,
    ]
  );

  const selected =
    value === undefined || value === null || value === ""
      ? null
      : value === placeholderOption.value
      ? placeholderOption
      : { label: value, value };

  return (
    <div className="commodity-select__input batch-number-select">
      <AsyncCreatableSelect
        className="wfp--react-select-container"
        classNamePrefix="wfp--react-select"
        // A change of query has to remount, or react-select keeps serving the
        // options it prefetched for the previous one.
        key={`${commodityId}.${dispatchMonth}`}
        styles={styles}
        value={selected}
        onChange={(option: BatchOption) => onChange(option.value)}
        onBlur={onBlur}
        onCreateOption={(input: string) =>
          onChange(resolveBatchNumber(input, loadedOptions.current))
        }
        defaultOptions
        loadOptions={loadOptions}
      />
    </div>
  );
};

export default BatchNumberSelect;
